import type { ToolOutput } from '../../services/api'

/**
 * The pure data half of `ChartMessage` (MAR-173 PR 3): what a chart output's
 * `null` and `partial` mean, kept out of the component file so it can be tested
 * without drawing anything.
 */

export type ChartData = NonNullable<ToolOutput['data']>

/**
 * Which buckets are partial, per label. A bucket is partial if the chart says
 * so for its label (`data.partial`) or any dataset says so for its point
 * (`dataset.partial`). A report's first and last buckets are usually cut by the
 * window, and drawing them like whole ones reads as a collapse at both ends.
 */
export function partialBuckets(data: ChartData): boolean[] {
  return data.labels.map(
    (_, i) => data.partial?.[i] === true || data.datasets.some((d) => d.partial?.[i] === true)
  )
}

/**
 * A pie's slices, from the first dataset. A missing value is left out rather
 * than drawn as a zero share of something.
 */
export function pieRows(data: ChartData): Array<{ name: string; value: number }> {
  return data.labels
    .map((label, index) => ({ name: label, value: data.datasets[0]?.data[index] }))
    .filter((d): d is { name: string; value: number } => typeof d.value === 'number' && Number.isFinite(d.value))
}

/**
 * The rows recharts draws. ⚠️ A missing point stays `null` — never `0`, never
 * `undefined`-coerced. merchant-chat's `salesChart` sends `null` for a bucket a
 * currency's series does not carry, and a line through 0 there would be a sales
 * figure nobody reported. With `connectNulls={false}` a `null` is a gap.
 */
export function chartRows(data: ChartData): Array<Record<string, string | number | null>> {
  return data.labels.map((label, index) => {
    const point: Record<string, string | number | null> = { name: label }
    data.datasets.forEach((dataset) => {
      const value = dataset.data[index]
      point[dataset.label] = typeof value === 'number' && Number.isFinite(value) ? value : null
    })
    return point
  })
}

/** Whether any series is missing any point — a `null`, or no value at all. */
export function hasGap(data: ChartData): boolean {
  return data.datasets.some((dataset) =>
    data.labels.some((_, i) => {
      const value = dataset.data[i]
      return typeof value !== 'number' || !Number.isFinite(value)
    })
  )
}
